import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock3,
  ChevronDown,
  ChevronRight,
  ArrowBigUp,
  Brain,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubmissionsForum = ({ submissions = [], teamMembers = [], token }) => {
  const [userCache, setUserCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [forumOpen, setForumOpen] = useState(true);
  const [threadOpen, setThreadOpen] = useState({});
  const [voteData, setVoteData] = useState({}); // { subId: { voted, count } }

  // ======================================================
  // LOAD INITIAL VOTE STATUS FROM BACKEND
  // ======================================================
  const loadVoteStatus = async (submissionId) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/engagements/submissions/${submissionId}/vote`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) return null;
      return await res.json(); // { voted, voteCount }
    } catch (err) {
      console.error("❌ Vote status fetch failed:", err);
      return null;
    }
  };

  useEffect(() => {
    const loadAllVotes = async () => {
      const initial = {};
      for (const sub of submissions) {
        const status = await loadVoteStatus(sub.id);
        initial[sub.id] = {
          voted: status?.voted || false,
          count: status?.voteCount || 0,
        };
      }
      setVoteData(initial);
    };

    if (submissions.length) loadAllVotes();
  }, [submissions]);


  // ======================================================
  // TOGGLE VOTE → WITH BACKEND CALL
  // ======================================================
  const handleVote = async (subId) => {
    const token = localStorage.getItem("token");

    // Optimistic UI
    setVoteData((prev) => {
      const entry = prev[subId];
      const newVoted = !entry.voted;
      const newCount = entry.count + (newVoted ? 1 : -1);

      return { ...prev, [subId]: { voted: newVoted, count: newCount } };
    });

    try {
      await fetch(
        `${BACKEND_URL}/engagements/submissions/${subId}/vote`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }
        }
      );
    } catch (err) {
      console.error("❌ Vote toggle failed:", err);
    }
  };


  // ======================================================
  // USER FETCHING (UNCHANGED)
  // ======================================================
  const fetchUser = async (uid, email) => {
    try {
      if (uid) {
        const res = await fetch(`${BACKEND_URL}/user/uid/${uid}`);
        if (res.ok) return await res.json();
      }
      if (email) {
        const res = await fetch(`${BACKEND_URL}/user/${email}`);
        if (res.ok) return await res.json();
      }
      return null;
    } catch (err) {
      console.error("❌ User fetch error:", err);
      return null;
    }
  };

  useEffect(() => {
    const loadUsers = async () => {
      const cache = {};

      for (const sub of submissions) {
        const key = sub.userId || sub.userEmail;

        if (cache[key]) continue;

        const found = teamMembers.find(
          (m) =>
            m.uid === sub.userId ||
            m.email?.toLowerCase() === sub.userEmail?.toLowerCase()
        );

        if (found) {
          cache[key] = {
            name: `${found.firstName || ""} ${found.lastName || ""}`.trim(),
            profileImage: found.profileImage,
          };
          continue;
        }

        const fetched = await fetchUser(sub.userId, sub.userEmail);

        cache[key] = fetched
          ? {
              name:
                `${fetched.firstName || ""} ${fetched.lastName || ""}`.trim() ||
                fetched.email?.split("@")[0],
              profileImage: fetched.profileImage,
            }
          : {
              name: (sub.userEmail || "User").split("@")[0],
              profileImage: null,
            };
      }

      setUserCache(cache);
      setLoading(false);
    };

    if (submissions.length) loadUsers();
  }, [submissions, teamMembers]);


  // ======================================================
  // RENDER
  // ======================================================
  if (!submissions.length)
    return (
      <div className="text-sm text-gray-500 italic pl-3">
        No discussions yet.
      </div>
    );

  if (loading)
    return (
      <div className="text-[12px] text-gray-400 px-3 mt-2 italic">Loading…</div>
    );

  return (
    <div className="mt-2">
      {/* Header */}
      <div
        className="flex items-center justify-between px-1 cursor-pointer select-none"
        onClick={() => setForumOpen(!forumOpen)}
      >
        <div className="flex items-center gap-2">
          {forumOpen ? (
            <ChevronDown size={16} className="text-indigo-500" />
          ) : (
            <ChevronRight size={16} className="text-indigo-500" />
          )}
          <span className="text-xs font-semibold text-gray-700 uppercase">
            Threads
          </span>
        </div>

        <span className="text-[11px] text-gray-500">
          {submissions.length} posts
        </span>
      </div>

      {/* Body */}
      <AnimatePresence initial={false}>
        {forumOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ type: "spring", stiffness: 160, damping: 22 }}
            className="overflow-hidden"
          >
            <div className="space-y-4 max-h-60 overflow-y-auto pr-2 mt-2">
              {submissions.map((sub) => {
                const info = userCache[sub.userId || sub.userEmail] || {};
                const vote = voteData[sub.id] || { voted: false, count: 0 };
                const opened = threadOpen[sub.id] ?? true;

                const initials = (info.name || "U")
                  .split(" ")
                  .map((x) => x[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase();

                return (
                  <motion.article
                    key={sub.id}
                    layout
                    className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm"
                  >
                    {/* Header */}
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() =>
                        setThreadOpen((prev) => ({
                          ...prev,
                          [sub.id]: !opened,
                        }))
                      }
                    >
                      <div className="flex items-center gap-3">
                        {opened ? (
                          <ChevronDown size={14} className="text-gray-500" />
                        ) : (
                          <ChevronRight size={14} className="text-gray-500" />
                        )}

                        {info.profileImage ? (
                          <img
                            src={info.profileImage}
                            className="h-7 w-7 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-7 w-7 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center text-xs font-bold">
                            {initials}
                          </div>
                        )}

                        <span className="text-xs font-semibold text-gray-800">
                          {info.name || "Unknown"}
                        </span>
                      </div>

                      <div className="flex items-center gap-1 text-[11px] text-gray-400">
                        <Clock3 size={12} />
                        {new Date(sub.updatedAt).toLocaleTimeString()}
                      </div>
                    </div>

                    {/* Body */}
                    <AnimatePresence>
                      {opened && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ type: "spring", stiffness: 150 }}
                          className="overflow-hidden"
                        >
                          <p className="text-sm text-gray-700 mt-3 mb-2 whitespace-pre-line">
                            {sub.description}
                          </p>

                          {/* Vote + Insight */}
                          <div className="flex items-center justify-between mt-3">
                            {/* Upvote */}
                            <div className="flex items-center">
                              <button
                                onClick={() => handleVote(sub.id)}
                                className={`p-1 rounded-md transition ${
                                  vote.voted
                                    ? "text-indigo-600 bg-indigo-50"
                                    : "text-gray-400 hover:text-gray-600"
                                }`}
                              >
                                <ArrowBigUp size={16} />
                              </button>

                              <span className="ml-1 text-xs font-semibold text-gray-700">
                                {vote.count}
                              </span>
                            </div>

                            {/* Insight */}
                            <button className="flex items-center gap-1 text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-full px-2 py-[2px]">
                              <Brain size={12} />
                              Insight
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.article>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubmissionsForum;
